//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import { useTableCurrentPage } from '../../../../hooks/useTableCurrentPage';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel,
  PaginationState,
  VisibilityState,
  FilterFnOption,
  Row,
} from '@tanstack/react-table';
import { format, formatISO, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import isEqual from 'lodash.isequal';

// Double Tap Support
import { DoubleTapRow } from '../components/DoubleTapRow';
import { SortableHeader } from '../components/SortableHeader';

// Styled Components
import {
  TrStyled,
  TableContainerStyled,
  SubContainerFiltersStyled,
  TdStyled,
  SubContainerHead,
  ShowFiltersButton,
  ArrowBottomStyled,
  DropDownInputStyledBig,
  ContainerButtonStyled,
  ContainerStyled,
  ContainerLoader,
  FiltersWithLoadingStyled,
} from './styles';
import {
  Th,
  Tbody,
  Title,
  Table,
  TextCell,
  TextHeader,
  InputRadioContainer,
  InputRadioStyled,
  InputCheckContainer,
  CheckBoxStyled,
  InputCalendarStyled,
  SearchableDropdownStyled,
  MultiSelectDropdownStyled,
} from '../components/MainStyledComponents';

// Components
import { CheckBox } from '../../../inputs/CheckBox';
import { PaginationButtons } from '../components/PaginationButtons';
import { SelectionBanner } from '../components/SelectionBanner';
import InputText from '../../../inputs/InputText';
import InputExpedient from '../../../inputs/InputExpedient';
import InputIndex from '../../../inputs/InputIndex';
import InputPK from '../../../inputs/InputPK';
import { DropDownVisibilityColumn } from '../components/DropDownVisibilityColumn';
import { createFlagColumn, exportIgnoreProps } from '../components/flagColumn';
import { useFlagScopeTexts } from '../../../../hooks/useFlagScopeTexts';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';
import {
  getLaneCutFilterOptions,
  laneCutSelectionToBoolean,
  booleanToLaneCutSelection,
} from '../../../../utils/laneCut';
import IncludeInactiveToggle from '../../../inputs/IncludeInactiveToggle';
import { SpecialTag } from '../../SpecialTag';

// Types
import {
  columnID,
  columnNames,
  getColumnNames,
  getColumnId,
} from '../components/types';
import { tableProps, dataTable, option } from './types';
import { onChangeValue } from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import { ImperativeRefSearchableDropdown } from '../../../inputs/SearchableDropdown/types';
import { takeBase } from '../types';
import { useServerPageSize } from '../utils/useServerPageSize';
import { useMirroredCursor } from '../utils/useMirroredCursor';
import {
  ImperativeRefInputExpedient,
  onChangeInputExpedientTypes,
} from '../../../inputs/InputExpedient/types';
import {
  ImperativeRefInputIndex,
  onChangeInputIndexTypes,
} from '../../../inputs/InputIndex/types';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';
import { size, variant } from '../../SpecialTag/types';
import { Order } from '../../../../types/orders';
import { Role } from '../../../../types/auth';
import { OrderState } from '../../../../types/orders';

// Utils
import { roidFilterFunction, dateFilterFunction } from './utils';
import { formatRoidForDisplay } from '../utils/roidFormatter';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
} from '../../../../hooks/useMediaBreakPoints';

// GraphQL
import { client } from '../../../../gql/client';
import {
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_SUB_CAPITOLS_BY_IDS,
} from '../../../../gql/queries/orders/predefinedData';

// Redux
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { generateRoidPadding, getStateOptionsByRoleAndEntityType, getContainerTypeOptions } from '../../../../utils/order';
import { TABLE_ENTITY_TYPES, CONTAINER_SCOPED_TABLE_IDS, listingFiltersByRole } from '../types';
import { Loader } from '../../../inputs/Loader';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { filterAndSortMultimediaUsers } from '../../../../utils/multimediaUserFilters';

// Columnas ordenables server-side → campo del backend (whitelist Accident)
const SORTABLE_COLUMNS = {
  state: 'state',
  registerInitDate: 'registerInitDate',
  creationDate: 'creationDate',
  pkInit: 'pkInitKm',
  pkEnd: 'pkEndKm',
  // Campo denormalizado en el modelo Accident y presente en la whitelist
  // ACCIDENT_SORTABLE_FIELDS del backend (espejo de GenericOrder/Action).
  direction: 'direction',
} as const;
const DEFAULT_SORT_FIELD = 'registerInitDate';

/**
 * Por defecto la bandera marca la Order de la fila: la fila de accidente ya
 * lleva su id (`selectAccidentOrders` → `order.id`). Fuera del componente
 * para que la referencia sea estable y no rehaga las columnas en cada render.
 */
const defaultGetFlagOrderId = (row: dataTable): string | undefined =>
  row.id || undefined;

export const AccidentsTable = (allProps: tableProps): JSX.Element => {
  const {
    titleText,
    data,
    onReFetchData,
    onReFetchPagination,
    tableId,
    onRowOpen,
    // Fuera de `props`: no son atributos del <table>.
    showFlagColumn = false,
    showOnlyFlaggedFilter = false,
    getFlagOrderId = defaultGetFlagOrderId,
    ...props
  } = allProps;

  const isCheckboxMode = allProps.selectionMode === 'checkbox';

  // Checkbox mode: controlled from parent (no internal rowSelection state)
  const controlledRowSelection = isCheckboxMode ? (allProps as any).rowSelection : undefined;
  const controlledOnChange = isCheckboxMode ? (allProps as any).onRowSelectionChange : undefined;
  const selectAllMode = isCheckboxMode ? (allProps as any).selectAllMode ?? false : false;
  const excludedIds = isCheckboxMode ? (allProps as any).excludedIds : undefined;
  const onSelectAll = isCheckboxMode ? (allProps as any).onSelectAll : undefined;
  const onClearSelection = isCheckboxMode ? (allProps as any).onClearSelection : undefined;
  const propsSelectedCount = isCheckboxMode ? (allProps as any).selectedCount : 0;

  // Radio mode: setRowState callback
  const radioSetRowState = !isCheckboxMode ? (allProps as any).setRowState : undefined;
  const { t } = useTranslation(['tables', 'common', 'accidents', 'forms']);
  const flagScopeTexts = useFlagScopeTexts();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const globalCursor = useSelector(getCursor(tableId), isEqual);
  const isLoadingData = useSelector(getIsLoadingData(tableId), isEqual);
  const dataAll = data.accidents;

  const inputListConRef = useRef<ImperativeRefInput>(null);
  const inputWeatherRef = useRef<ImperativeRefInput>(null);
  const inputMediumKnowledge = useRef<ImperativeRefInput>(null);
  const inputListClassificationRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListCapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListSubcapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListPredefinedRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputStateRef = useRef<ImperativeRefMultiSelect>(null);
  const inputLaneCutRef = useRef<ImperativeRefInput>(null);
  const inputUserRef = useRef<ImperativeRefMultiSelect>(null);
  // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen".
  const inputContainerTypeRef = useRef<ImperativeRefMultiSelect>(null);
  const inputTeamRef = useRef<ImperativeRefMultiSelect>(null);
  const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputExpedientRef = useRef<ImperativeRefInputExpedient>(null);
  const inputIndexRef = useRef<ImperativeRefInputIndex>(null);
  const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
  const inputPKEndRef = useRef<ImperativeRefInputPK>(null);

  const isLanscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  const showCapitolFilter = useFeatureFlag('filter_show_capitol', true);
  const showSubCapitolFilter = useFeatureFlag('filter_show_subcapitol', true);
  const showPredefinedFilter = useFeatureFlag('filter_show_predefined', true);

  const [expedientDefault, setExpedientDefault] = useState<number | undefined>(
    undefined
  );
  const [indexDefault, setIndexDefault] = useState<number | undefined>(
    undefined
  );
  const [showFilters, setShowFilters] = useState(false);
  // In checkbox mode: controlled from parent. In radio mode: internal state.
  const [internalRowSelection, setInternalRowSelection] = useState({});
  const rowSelection = isCheckboxMode ? controlledRowSelection : internalRowSelection;
  const setRowSelection = isCheckboxMode ? controlledOnChange : setInternalRowSelection;
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // C-11.7: persistido en Redux para mantener coherencia paginador↔cursor
  // al navegar a un detail y volver. Ver hooks/useTableCurrentPage.
  const [currentServerPage, setCurrentServerPage] = useTableCurrentPage(
    tableId as string,
  );
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });
  const [selectorCursor, setSelectorCursor] = useMirroredCursor<{
    description: string | undefined; // filtro por descripción (texto libre)
    take: number;
    cursor: string;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    weatherId: string | undefined;
    mediumKnowledgeId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat with cursor shape)
    states: string[] | undefined; // multi-select (Plan 2 Tarea: filtro estado multi-select)
    userIds: string[] | undefined; // multi-select creador (Plan 2 Tarea 14)
    containerTypes: string[] | undefined; // multi-select "Origen" (Fase 4 nav Trabajo/Revisión)
    teamIds: string[] | undefined; // multi-select equipo (heredado del expediente padre)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador" (Plan Task 7)
    // `undefined` = no lo ha tocado (manda el cursor) · `null` = eligió
    // «Todos» a propósito (limpia el filtro) · `true`/`false` = filtra.
    laneCutting: boolean | null | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    sortField: string | undefined;
    orderBy: 'asc' | 'desc' | undefined;
    onlyFlagged: true | undefined; // «Solo marcadas»: true o undefined, nunca false
  }>(globalCursor);

  // Tarea #11 — page-size selector centralizado en `useServerPageSize`. La
  // tabla siempre está en su contexto principal (no se usa en modales) → el
  // gate `enabled` es true. La regla "tablet usa takeBase" la aplica el hook.
  const initialSubCapitolsOptions = data?.filters?.subCapitols?.map(
    (subCapitol: { name: string; id: string }) => ({
      label: subCapitol.name,
      value: subCapitol.id,
    })
  );

  const [subCapitolOptions, setSubCapitolOptions] = useState(
    initialSubCapitolsOptions
  );

  const [selectorData, setSelectorData] = useState<{
    description: string | undefined; // filtro por descripción (texto libre)
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    weatherId: string | undefined;
    mediumKnowledgeId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat)
    states: string[] | undefined; // multi-select
    userIds: string[] | undefined; // multi-select creador (Plan 2 Tarea 14)
    containerTypes: string[] | undefined; // multi-select "Origen" (Fase 4 nav Trabajo/Revisión)
    teamIds: string[] | undefined; // multi-select equipo (heredado del expediente padre)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador" (Plan Task 7)
    // `undefined` = no lo ha tocado (manda el cursor) · `null` = eligió
    // «Todos» a propósito (limpia el filtro) · `true`/`false` = filtra.
    laneCutting: boolean | null | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
  }>({
    description: undefined,
    indexType: undefined,
    indexExpedition: undefined,
    concessionId: undefined,
    fromDate: undefined,
    toDate: undefined,
    classification: undefined,
    capitolId: undefined,
    subCapitolId: undefined,
    predefinedOrderId: undefined,
    weatherId: undefined,
    mediumKnowledgeId: undefined,
    state: undefined,
    states: undefined,
    userIds: undefined,
    containerTypes: undefined,
    teamIds: undefined,
    includeInactive: undefined,
    laneCutting: undefined,
    pkInitKm: undefined,
    pkInitMeter: undefined,
    pkEndKm: undefined,
    pkEndMeter: undefined,
  });

  // `takePagination` reemplaza el antiguo `serverPageSize` y `paginationProps`
  // se esparce en `<PaginationButtons>` para que el selector solo aparezca en
  // escritorio. `liveFilters: selectorData` para respetar filtros del form
  // aún no comprometidos a Redux al cambiar el take.
  const { takePagination, paginationProps } = useServerPageSize({
    tableId,
    enabled: true,
    selectorCursor,
    liveFilters: selectorData as unknown as Record<string, unknown>,
    setCurrentServerPage,
    onReFetchData,
  });

  const roleActive = data.roleActive;

  // NOTE: Accidents does NOT apply default state filter
  // All accidents should be visible without state filtering
  // @see BUG #9 in TRACKING_BUGS_TESTING_2026.md

  // Fase 4 nav Trabajo/Revisión (2026-07-12) — la columna+filtro "Origen" es
  // redundante en las pestañas CONTEXTUALES (campaña/RV): la página ya está
  // fijada a un container concreto o a un tipo fijo. Se deriva del `tableId`
  // (no una prop nueva) — mismo criterio que `TABLE_ENTITY_TYPES`.
  const isContainerScopedTable = CONTAINER_SCOPED_TABLE_IDS.has(
    tableId as string
  );

  const handleClick = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  useEffect(() => {
    isLanscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLanscapeTablet]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    containerType: !isContainerScopedTable,
  });

  useEffect(() => {
    setColumnVisibility(prev => ({
      ...prev,
      predefinedOrder: showPredefinedFilter,
    }));
  }, [showPredefinedFilter]);

  useEffect(() => {
    const concessions = data?.filters?.concessions;
    setColumnVisibility(prev => ({
      ...prev,
      concession: !concessions || concessions.length > 1,
    }));
  }, [data?.filters?.concessions]);

  const [sizeTag, setSizeTag] = useState<size>(
    isPortraitTablet ? 'circle' : 'small'
  );

  const callbackSetRoid = useCallback(async () => {
    let classification;
    if (selectorCursor?.classification) {
      const classificationRaw = await client.query({
        query: GET_CLASSIFICATION_BY_ID_OR_ROID,
        variables: {
          id: selectorCursor?.classification,
        },
      });
      classification =
        classificationRaw?.data?.classification?.classificationCode;
    }
    // Actualizar valores por defecto desde el cursor
    setExpedientDefault(
      selectorCursor?.indexExpedition !== undefined &&
        selectorCursor?.indexExpedition >= 0
        ? selectorCursor.indexExpedition
        : undefined
    );
    setIndexDefault(
      selectorCursor?.indexType !== undefined && selectorCursor?.indexType >= 0
        ? selectorCursor.indexType
        : undefined
    );
  }, [selectorCursor]);

  useEffect(() => {
    callbackSetRoid();
  }, [selectorCursor]);

  useEffect(() => {
    isLanscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLanscapeTablet]);

  useLayoutEffect(() => {
    setSizeTag(isPortraitTablet ? 'circle' : 'small');
  }, [isPortraitTablet]);

  const columnHelper = createColumnHelper<dataTable>();
  const translatedColumnNames = useMemo(() => getColumnNames(t), [t]);

  // Ordenación server-side: campo activo y dirección derivados del cursor Redux
  const activeSortField = selectorCursor?.sortField ?? DEFAULT_SORT_FIELD;
  const activeSortColumnId =
    (Object.keys(SORTABLE_COLUMNS) as (keyof typeof SORTABLE_COLUMNS)[]).find(
      (k) => SORTABLE_COLUMNS[k] === activeSortField,
    ) ?? 'registerInitDate';
  const sortDirection: 'asc' | 'desc' =
    selectorCursor?.orderBy === 'asc' ? 'asc' : 'desc';

  const onSortColumn = useCallback(
    (sortField: string) => {
      const isActive = activeSortField === sortField;
      const nextOrderBy: 'asc' | 'desc' =
        isActive && sortDirection === 'desc' ? 'asc' : 'desc';
      // Canonicalización: registerInitDate + desc viaja como sortField: undefined EXPLÍCITO
      const isDefault =
        sortField === DEFAULT_SORT_FIELD && nextOrderBy === 'desc';
      const dataCursor = {
        sortField: isDefault ? undefined : sortField,
        orderBy: nextOrderBy,
        cursor: '',
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setCurrentServerPage(1);
      onReFetchData?.();
    },
    [activeSortField, sortDirection, dispatch, tableId, setCurrentServerPage, onReFetchData],
  );

  // Helper que evita la duplicación en las columnas ordenables.
  const sortableHeader = useCallback(
    (columnId: keyof typeof SORTABLE_COLUMNS) => () => (
      <SortableHeader
        label={translatedColumnNames[columnId as keyof typeof translatedColumnNames]?.label ?? columnId}
        active={activeSortColumnId === columnId}
        direction={sortDirection}
        onToggle={() => onSortColumn(SORTABLE_COLUMNS[columnId])}
      />
    ),
    [translatedColumnNames, activeSortColumnId, sortDirection, onSortColumn],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        ...(isCheckboxMode
          ? {
              header: ({ table }) => (
                <InputCheckContainer>
                  <CheckBox
                    name={'select'}
                    {...{
                      checked: table.getIsAllRowsSelected(),
                      indeterminate: table.getIsSomeRowsSelected(),
                      onChange: table.getToggleAllRowsSelectedHandler(),
                    }}
                  />
                </InputCheckContainer>
              ),
              cell: ({ row }) => (
                <InputCheckContainer
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <CheckBoxStyled
                    {...{
                      checked: row.getIsSelected(),
                      disabled: !row.getCanSelect(),
                      indeterminate: row.getIsSomeSelected(),
                      onChange: row.getToggleSelectedHandler(),
                    }}
                  />
                </InputCheckContainer>
              ),
            }
          : {
              cell: ({ row }) => (
                <InputRadioContainer
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <InputRadioStyled
                    name="inputRadio"
                    {...{
                      checked: row.getIsSelected(),
                      disabled: !row.getCanSelect(),
                      indeterminate: row.getIsSomeSelected(),
                      onChange: row.getToggleSelectedHandler(),
                    }}
                  />
                </InputRadioContainer>
              ),
            }),
      }),
      // Banderitas (spec 2026-10-22 § 8.2): justo detrás de la selección y
      // solo si la pantalla la pide.
      ...(showFlagColumn
        ? [
            createFlagColumn<dataTable>(
              getFlagOrderId),
          ]
        : []),
      columnHelper.accessor(getColumnId(translatedColumnNames.state.id), {
        header: sortableHeader('state'),
        cell: (info) => (
          <SpecialTag sizeTag={sizeTag} variant={info.getValue() as variant} />
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.roid.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.roid.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>
            {formatRoidForDisplay(info.getValue())}
          </TextCell>
        ),
        filterFn: 'roidFilter' as FilterFnOption<Order>, //Indicate the filter function to be used for this column
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registerInitDate.id),
        {
          header: sortableHeader('registerInitDate'),
          cell: (info) => (
            <TextCell size={'S'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCell>
          ),
          filterFn: 'dateFilter' as FilterFnOption<dataTable>,
        }
      ),

      columnHelper.accessor(
        getColumnId(translatedColumnNames.mediumKnowledge.id),
        {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.mediumKnowledge.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size={'S'}>{info.getValue() as string}</TextCell>
          ),
        }
      ),

      columnHelper.accessor(getColumnId(translatedColumnNames.weather.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.weather.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),

      columnHelper.accessor((row) => row.isLaneCut ?? false, {
        id: getColumnId(translatedColumnNames.laneCut.id),
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.laneCut.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>
            {info.getValue()
              ? t('forms:actionEdition.yes')
              : t('forms:actionEdition.no')}
          </TextCell>
        ),
      }),

      columnHelper.accessor(getColumnId(translatedColumnNames.predefinedOrder.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.predefinedOrder.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),

      columnHelper.accessor(getColumnId(translatedColumnNames.concession.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.concession.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.road.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.road.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      // Sentido: mismo patrón que la tabla de OTs (InputRadioTagTable). La
      // celda pinta el valor CRUDO — el formato "Texto (símbolo)" lo pone el
      // selector (`selectAccidentOrders` → formatDirectionCellLabel), igual
      // que en el resto de listados. Ordenable server-side: `direction` está
      // en ACCIDENT_SORTABLE_FIELDS del backend.
      columnHelper.accessor(getColumnId(translatedColumnNames.direction.id), {
        header: sortableHeader('direction'),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkInit.id), {
        header: sortableHeader('pkInit'),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkEnd.id), {
        header: sortableHeader('pkEnd'),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.user.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.user.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>
            {(info.getValue() as string) || ''}
          </TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.registeredBy.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.registeredBy.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{(info.getValue() as string) || ''}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.description.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.description.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      // Fase 4 nav Trabajo/Revisión (2026-07-12) — columna "Origen"
      // (containerType traducido con la label EXISTENTE `common:orderTypes.*`,
      // servido directo por el backend — ver gql/fragments/accident.ts).
      // Oculta por defecto en pestañas contextuales vía
      // `columnVisibility.origin` (gating por tableId).
      columnHelper.accessor(getColumnId(translatedColumnNames.containerType.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.containerType.label}
          </TextHeader>
        ),
        cell: (info) => {
          const value = info.getValue() as string | undefined;
          return (
            <TextCell size={'S'}>
              {value ? t(`common:orderTypes.${value}`) : ''}
            </TextCell>
          );
        },
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.creationDate.id),
        {
          header: sortableHeader('creationDate'),
          cell: (info) => (
            <TextCell size={'S'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCell>
          ),
        }
      ),
    ],
    [
      isPortraitTablet,
      sizeTag,
      translatedColumnNames,
      t,
      sortableHeader,
      showFlagColumn,
      getFlagOrderId,
    ]
  );

  // Tarea #11 — Sincronizar pageSize de TanStack con `takePagination` (server).
  // Sin esto, TanStack pagina internamente con `pageSize` literal (15), pero
  // los botones de paginación hacen server fetch — el usuario solo vería 15
  // de las (hasta 100) filas fetched. Ligamos ambos para que TanStack rendee
  // todo lo que el server trajo en una sola página interna.
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize: takePagination,
    }),
    [pageIndex, takePagination]
  );

  const filterFns = {
    roidFilter: roidFilterFunction,
    dateFilter: dateFilterFunction,
  };

  const table = useReactTable({
    getRowId: (row: any) => row.id,
    data: dataAll,
    columns,
    state: {
      rowSelection,
      columnFilters,
      pagination,
      columnVisibility,
    },
    filterFns,
    manualSorting: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true, //enable row selection for all rows
    enableMultiRowSelection: isCheckboxMode,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
  });

  const mediumOptions = data.filters.mediumKnowledges.map(
    (medium: { name: string; id: string }) => ({
      label: medium.name,
      value: medium.id,
    })
  );

  const concessionOptions = data.filters.concessions.map(
    (concession: { name: string; id: string }) => ({
      label: concession.name,
      value: concession.id,
    })
  );

  // Auto-select the only concession if there's only one available
  useEffect(() => {
    if (
      concessionOptions &&
      concessionOptions.length === 1 &&
      !selectorCursor.concessionId
    ) {
      const onlyConcession = concessionOptions[0];
      setSelectorCursor((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
      setSelectorData((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
    }
  }, [concessionOptions, selectorCursor.concessionId]);

  const classificationOptions = useCallback(() => {
    return data?.filters?.classifications
      ?.slice()
      ?.sort((a, b) => (a.classificationCode ?? 0) - (b.classificationCode ?? 0))
      ?.map((classification) => ({
        label: `${classification.classificationCode} - ${classification.name}`,
        value: classification.id,
      }));
  }, [data?.filters?.classifications]);

  const weatherOptions = data.filters.weathers.map(
    (weather: { name: string; id: string }) => ({
      label: weather.name,
      value: weather.id,
    })
  );

  const capitolOptions = useCallback(() => {
    return data?.filters?.capitols?.map((capitol) => ({
      label: capitol.name,
      value: capitol.id,
      subCapitolsIDs: capitol.subCapitolsIDs,
    }));
  }, [data?.filters?.capitols]);

  const predefinedOrderOptions = data?.filters?.predefinedOrders?.map(
    (predefined: { id: string; name: string }) => ({
      label: predefined.name,
      value: predefined.id,
    })
  );

  // User options for filtering (multi-select). Reusa la lógica de Multimedia:
  // solo usuarios activos con rol {SuperAdmin, Coex, Tablet}. Plan 2 Tarea 14.
  //
  // Plan Task 7: cuando `includeInactive=false` pero el cursor ya tiene
  // userIds seleccionados que NO están en la base activa (porque están
  // inactivos), se mergean como missing options para que los chips
  // sigan renderizando (edge case 7.1 del spec).
  const userOptions = useMemo(() => {
    const base = filterAndSortMultimediaUsers(
      data?.filters?.users ?? [],
      selectorCursor?.includeInactive ?? false,
    );
    const selectedIds = selectorCursor?.userIds ?? [];
    const missingIds = selectedIds.filter(
      (id: string) => !base.find((o) => o.value === id),
    );
    if (missingIds.length === 0) return base;
    const allUsers = data?.filters?.users ?? [];
    const missingOptions = missingIds
      .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
      .filter((u: unknown): u is { id?: string; name?: string; surName?: string; isActive?: boolean } => Boolean(u))
      .map((u) => ({
        label:
          u?.isActive === false
            ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
            : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
        value: u?.id ?? '',
      }));
    return [...base, ...missingOptions];
  }, [
    data?.filters?.users,
    selectorCursor?.includeInactive,
    selectorCursor?.userIds,
  ]);

  const teamOptions = useMemo(
    () =>
      data?.filters?.teams?.map((team) => ({
        label: team.name ?? '',
        value: team.id,
      })) ?? [],
    [data?.filters?.teams],
  );

  const onChangeInputExpedient = useCallback<onChangeInputExpedientTypes>(
    async (value) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor.cursor,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        indexType: selectorData.indexType ?? selectorCursor.indexType,
        indexExpedition: value,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.indexExpedition = value;
        return newData;
      });
      setExpedientDefault(value);
    },
    [
      selectorCursor,
      selectorData.concessionId,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.mediumKnowledgeId,
      selectorData.weatherId,
      selectorData?.states,
      selectorData.indexType,
      selectorData.classification,
      selectorData.capitolId,
      selectorData.subCapitolId,
      selectorData.predefinedOrderId,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangeInputIndex = useCallback<onChangeInputIndexTypes>(
    async (value) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor.cursor,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        indexType: value,
        indexExpedition:
          selectorData.indexExpedition ?? selectorCursor.indexExpedition,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.indexType = value;
        return newData;
      });
      setIndexDefault(value);
    },
    [
      selectorCursor,
      selectorData.concessionId,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.mediumKnowledgeId,
      selectorData.weatherId,
      selectorData?.states,
      selectorData.indexExpedition,
      selectorData.classification,
      selectorData.capitolId,
      selectorData.subCapitolId,
      selectorData.predefinedOrderId,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangePKInit = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkInitKm: km, pkInitMeter: meter }));
    },
    []
  );

  const onChangePKEnd = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkEndKm: km, pkEndMeter: meter }));
    },
    []
  );

  const onChangeDateInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromDateIso = formatISO(range.from as Date);
      const toDateIso = formatISO(range.to as Date);
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: fromDateIso,
        toDate: toDateIso,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.fromDate = fromDateIso;
        newData.toDate = toDateIso;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData.indexType,
      selectorData.indexExpedition,
      selectorData.concessionId,
      selectorData.classification,
      selectorData.capitolId,
      selectorData.subCapitolId,
      selectorData.predefinedOrderId,
      selectorData.mediumKnowledgeId,
      selectorData.weatherId,
      selectorData?.states,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onClearValue = useCallback(() => {
    inputDateRef.current?.clearSelected();
    inputExpedientRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputPKInitRef.current?.clearSelected();
    inputPKEndRef.current?.clearSelected();
    inputMediumKnowledge.current?.clearSelected();
    inputWeatherRef.current?.clearSelected();
    inputListClassificationRef.current?.clearSelected();
    inputListCapitolRef.current?.clearSelected();
    inputListSubcapitolRef.current?.clearSelected();
    inputListPredefinedRef.current?.clearSelected();
    inputStateRef.current?.clearSelected();
    inputLaneCutRef.current?.clearSelected();
    inputUserRef.current?.clearSelected();
    inputContainerTypeRef.current?.clearSelected();
    inputTeamRef.current?.clearSelected();
    setColumnFilters([]);

    // Check if there's only one concession
    const hasOnlyOneConcession = concessionOptions && concessionOptions.length === 1;
    const onlyConcessionId = hasOnlyOneConcession ? concessionOptions[0].value : undefined;

    // If there's only one concession, keep it selected
    // Otherwise, clear it
    if (!hasOnlyOneConcession) {
      inputListConRef.current?.clearSelected();
    }

    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: undefined,
      indexExpedition: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      capitolId: undefined,
      subCapitolId: undefined,
      predefinedOrderId: undefined,
      weatherId: undefined,
      mediumKnowledgeId: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      containerTypes: undefined,
      teamIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      laneCutting: undefined,
      orderBy: 'desc',
      sortField: undefined,
      // Explícito: el reducer hace merge y omitirlo conservaría el filtro.
      onlyFlagged: undefined,
    };
    dispatch(setCursor(tableId, dataCursor));
    setSelectorCursor((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      capitolId: undefined,
      subCapitolId: undefined,
      predefinedOrderId: undefined,
      weatherId: undefined,
      mediumKnowledgeId: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      containerTypes: undefined,
      teamIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      laneCutting: undefined,
      sortField: undefined,
      orderBy: 'desc',
      onlyFlagged: undefined,
    }));
    setExpedientDefault(undefined);
    setIndexDefault(undefined);
    setSelectorData((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      capitolId: undefined,
      subCapitolId: undefined,
      predefinedOrderId: undefined,
      weatherId: undefined,
      mediumKnowledgeId: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      containerTypes: undefined,
      teamIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      laneCutting: undefined,
    }));
    setSubCapitolOptions(initialSubCapitolsOptions);
    setCurrentServerPage(1);
    onReFetchData?.();
    onClearSelection?.(); // Bug #2: al cambiar/limpiar el filtro se reinicia la selección (no en paginación)
    // Tarea #11 fix: `takePagination` debe estar en deps para evitar stale closure.
  }, [concessionOptions, onChangeInputExpedient, onChangeInputIndex, takePagination]);

  const onChangeSelectedWeatherGroup = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        weatherId: option.value,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.weatherId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData.capitolId,
      selectorData.subCapitolId,
      selectorData.predefinedOrderId,
      selectorData.mediumKnowledgeId,
      selectorData.concessionId,
      selectorData?.states,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangeSelectedMediumGroup = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId: option.value,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.mediumKnowledgeId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData.indexType,
      selectorData.indexExpedition,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.classification,
      selectorData.capitolId,
      selectorData.subCapitolId,
      selectorData.predefinedOrderId,
      selectorData.concessionId,
      selectorData.weatherId,
      selectorData?.states,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangeSelectedConcession = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId: option.value,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.concessionId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData.capitolId,
      selectorData.subCapitolId,
      selectorData.predefinedOrderId,
      selectorData.mediumKnowledgeId,
      selectorData.weatherId,
      selectorData?.states,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
    ]
  );

  const onChangeSelectedStates = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by
      // `keyArgs: [..., 'states']` (accidents) produces the same key for the
      // same selection regardless of click order.
      const sorted = [...values].sort();
      const nextStates = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: nextStates,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, states: nextStates }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.predefinedOrderId,
      selectorData?.mediumKnowledgeId,
      selectorData?.weatherId,
      selectorData?.userIds,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  /**
   * Selector SIMPLE: solo actualiza `selectorData`; el filtro se aplica al
   * pulsar Buscar, igual que el resto de desplegables de esta barra. La
   * opción «Todos» (valor vacío) es la que permite VACIAR el filtro sin
   * tener que limpiar todos los demás con la papelera.
   */
  const onChangeSelectedLaneCutting = (value: option) => {
    setSelectorData((prevData) => ({
      ...prevData,
      // `?? null`: «Todos» tiene que ser DISTINTO de «sin tocar», o el
      // `??` de handleSearch caería al valor viejo del cursor y no limpiaría.
      laneCutting: laneCutSelectionToBoolean(value?.value) ?? null,
    }));
  };

  const onChangeSelectedUserIds = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by
      // `keyArgs: [..., 'userIds']` produces the same key for the same
      // selection regardless of click order.
      const sorted = [...values].sort();
      const nextUserIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: nextUserIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, userIds: nextUserIds }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.predefinedOrderId,
      selectorData?.mediumKnowledgeId,
      selectorData?.weatherId,
      selectorData?.states,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen" (containerType:
  // generic|campaign|surveillanceRoute). Mismo patrón que
  // `onChangeSelectedStates`/`onChangeSelectedUserIds` — canonicaliza con
  // sort estable para que `keyArgs: [..., 'containerTypes']` (policies.ts)
  // particione la caché de forma estable independientemente del orden de
  // click. Solo se monta en las tablas GLOBALES (ver gating en el render).
  const onChangeSelectedContainerTypes = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextContainerTypes = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        orderBy: 'desc',
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: nextContainerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, containerTypes: nextContainerTypes }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.predefinedOrderId,
      selectorData?.mediumKnowledgeId,
      selectorData?.weatherId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.teamIds,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  // #75 — filtro multi-select de Equipo (heredado del expediente padre).
  // Canonicaliza con sort estable (keyArgs: [..., 'teamIds'] en policies.ts).
  const onChangeSelectedTeamIds = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextTeamIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        orderBy: 'desc',
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes:
          selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: nextTeamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, teamIds: nextTeamIds }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.predefinedOrderId,
      selectorData?.mediumKnowledgeId,
      selectorData?.weatherId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.containerTypes,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  // Plan Task 7: toggle "Incluir inactivos en Creador".
  // Solo expone usuarios desactivados en el dropdown Creador; NO filtra
  // resultados del listado. Persiste en el cursor (Redux) y dispara
  // onReFetchData via useEffect aguas abajo.
  // «Solo marcadas» (spec 2026-10-22 § 8.3). Solo guarda en el cursor —el
  // reducer hace merge, así que basta con su clave— y la búsqueda la lanza
  // Buscar. Desmarcado es `undefined`, nunca `false`: así la caché de Apollo no
  // se parte en dos claves para «sin filtro».
  const onChangeOnlyFlagged = useCallback(
    (next: boolean) => {
      dispatch(setCursor(tableId, { onlyFlagged: next ? true : undefined }));
    },
    [dispatch, tableId]
  );

  const onChangeIncludeInactive = useCallback(
    (next: boolean) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId:
          selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: next,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        includeInactive: next,
      }));
    },
    [selectorCursor, selectorData, dispatch, tableId],
  );

  const onChangeSelectedClassification = (value: option) => {
    setSelectorData((prevData) => {
      const newData = prevData;
      newData.classification = value.value;
      return newData;
    });
  };

  const onChangeSelectedCapitol = useCallback(
    async (option: option) => {
      const {
        data: { readSubCapitols },
      } = await client.query({
        query: GET_SUB_CAPITOLS_BY_IDS,
        variables: {
          ids: (option as any).subCapitolsIDs,
        },
        fetchPolicy: 'cache-first',
      });
      const sortedSubCapitols = [...readSubCapitols].sort((a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name)
      );
      setSubCapitolOptions(
        sortedSubCapitols?.map((sc: { id: string; name: string }) => ({
          value: sc.id,
          label: sc.name,
        }))
      );
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: option.value,
        subCapitolId: undefined,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.capitolId = option.value;
        newData.subCapitolId = undefined;
        return newData;
      });

      inputListSubcapitolRef.current?.clearSelected();
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.weatherId,
      selectorData?.mediumKnowledgeId,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedSubCapitol = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        subCapitolId: option.value,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.subCapitolId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.predefinedOrderId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.weatherId,
      selectorData?.mediumKnowledgeId,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedPredefinedOrder = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        subCapitolId: selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        predefinedOrderId: option.value,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.predefinedOrderId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.weatherId,
      selectorData?.mediumKnowledgeId,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const handleClickRow = (row: Row<dataTable>) => {
    if (isCheckboxMode) {
      // Checkbox mode: toggle via TanStack (calls onRowSelectionChange → parent)
      row.toggleSelected(!row.getIsSelected());
    } else {
      // Radio mode: call parent's setRowState directly
      const val = row.getIsSelected();
      if (!val) {
        radioSetRowState?.(row.original.id || '');
      } else {
        radioSetRowState?.('');
      }
    }
  };

  // Sync rowSelection → setRowState for RADIO mode only
  // In checkbox mode, selection is controlled by parent via usePersistentSelection hook.
  useEffect(() => {
    if (isCheckboxMode) return; // Checkbox mode is fully controlled by parent
    const selectedIds = Object.keys(internalRowSelection).filter((key) => (internalRowSelection as Record<string, boolean>)[key]);
    if (selectedIds.length > 0) {
      radioSetRowState?.(selectedIds[0] || '');
    } else {
      radioSetRowState?.('');
    }
  }, [internalRowSelection, radioSetRowState, isCheckboxMode]);

  // Columnas que ofrece el desplegable «Columnas»: todas menos la de selección
  // (la primera) y las que no se pueden ocultar (la bandera, § 8.2). Sin
  // bandera queda igual que el `.slice(1)` de siempre.
  const hideableColumns = table
    .getAllColumns()
    .slice(1)
    .filter((column) => column.getCanHide());

  const hiddenColumns: number = Object.values(
    columnVisibility as object
  ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

  // commonColumns definition removed

  // commonLabels definition removed

  const fetchAccidentID = useCallback(
    async (id: string, take = takePagination) => {
      const dataSelector = {
        take,
        cursor: id,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        // Espejo de ValidationsTable.fetchOrderID: el cursor de paginación
        // viaja con el orden activo para que la request pivote y el loader
        // lean la MISMA partición Apollo (keyArgs incluyen sortField/orderBy).
        sortField: selectorCursor?.sortField,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
        weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
        mediumKnowledgeId:
          selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData.userIds ?? selectorCursor.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
        pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
        pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
        pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      };

      if (take > 0) {
        const lastAccident = dataAll[dataAll.length - 1].accidentID;
        if (lastAccident) {
          dispatch(setCursor(tableId, dataSelector));
        }
      } else {
        const firstAccident = dataAll[0].accidentID;

        if (firstAccident) {
          dispatch(setCursor(tableId, dataSelector));
        }
      }

      return dataSelector;
    },
    [
      selectorCursor,
      selectorData.indexType,
      selectorData.indexExpedition,
      selectorData.concessionId,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.classification,
      selectorData.capitolId,
      selectorData.subCapitolId,
      selectorData.predefinedOrderId,
      selectorData.weatherId,
      selectorData.mediumKnowledgeId,
      selectorData.states,
      selectorData.pkInitKm,
      selectorData.pkEndKm,
      dataAll,
      takePagination,
    ]
  );
  const nextPagination = async () => {
    // Clamp defensivo contra bug off-by-one en backend/cache (hasNextPage true
    // en última página real). Ver `useServerPageSize`/`policies.ts`.
    const pageCount = Math.max(
      1,
      Math.ceil((data?.totalCount ?? 0) / takePagination),
    );
    console.log(`%c[PAGE-NEXT] page=${currentServerPage}, hasNext=${data.pageInfo.hasNextPage}, cursor=${dataAll[dataAll.length - 1]?.accidentID}, dataAll.length=${dataAll.length}`, 'color: green; font-weight: bold');
    if (
      dataAll.length >= 1 &&
      data.pageInfo.hasNextPage &&
      currentServerPage < pageCount
    ) {
      setCurrentServerPage((prev) => Math.min(prev + 1, pageCount));
      await fetchAccidentID(dataAll[dataAll.length - 1].accidentID as string);
      onReFetchPagination();
    }
  };

  const backPagination = async () => {
    console.log(`%c[PAGE-BACK] page=${currentServerPage}, hasPrev=${data.pageInfo.hasPreviousPage}, cursor=${dataAll[0]?.accidentID}, dataAll.length=${dataAll.length}`, 'color: green; font-weight: bold');
    if (dataAll.length >= 1 && data.pageInfo.hasPreviousPage) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchAccidentID(dataAll[0].accidentID as string, -takePagination);
      onReFetchPagination();
    }
  };


  const handleSearch = async () => {
    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: selectorData?.indexType ?? selectorCursor?.indexType,
      indexExpedition:
        selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
      fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
      toDate: selectorData?.toDate ?? selectorCursor?.toDate,
      concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
      classification:
        selectorData?.classification ?? selectorCursor?.classification,
      capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
      subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
      predefinedOrderId: selectorData.predefinedOrderId ?? selectorCursor.predefinedOrderId,
      weatherId: selectorData.weatherId ?? selectorCursor.weatherId,
      mediumKnowledgeId:
        selectorData.mediumKnowledgeId ?? selectorCursor.mediumKnowledgeId,
      states: selectorData.states ?? selectorCursor.states,
      userIds: selectorData.userIds ?? selectorCursor.userIds,
      containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
      teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
      includeInactive:
        selectorData?.includeInactive ?? selectorCursor?.includeInactive,
      pkInitKm: selectorData.pkInitKm ?? selectorCursor.pkInitKm,
      pkInitMeter: selectorData.pkInitMeter ?? selectorCursor.pkInitMeter,
      pkEndKm: selectorData.pkEndKm ?? selectorCursor.pkEndKm,
      pkEndMeter: selectorData.pkEndMeter ?? selectorCursor.pkEndMeter,
      description:
        (selectorData.description ?? selectorCursor.description) || undefined,
      // `??` y NUNCA `||`: `false` es un filtro válido («No») y `||` lo
      // convertiría en «sin filtrar».
      laneCutting:
        selectorData?.laneCutting === null
          ? undefined
          : (selectorData?.laneCutting ?? selectorCursor?.laneCutting),
    };
    dispatch(setCursor(tableId as string, dataCursor));
    setCurrentServerPage(1);

    onReFetchData?.();
    onClearSelection?.(); // Bug #2: al buscar con filtro nuevo se reinicia la selección
  };

  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(filterContainerRef, handleSearch, onClearValue, isLoadingData as boolean);

  return (
    <ContainerStyled>
      {isLoadingData && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <FiltersWithLoadingStyled isLoading={isLoadingData}>
        <SubContainerHead>
          <Title component={'h2'} variant={'semiBold'} size={'M'}>
            {titleText}
          </Title>
          {isLanscapeTablet && (
            <ShowFiltersButton isBlack={showFilters} onClick={handleClick}>
              <ArrowBottomStyled isRotate={showFilters} />
            </ShowFiltersButton>
          )}
        </SubContainerHead>
        {showFilters && (
          <SubContainerFiltersStyled ref={filterContainerRef}>
            <InputExpedient
              label={t('headers.expedient')}
              disabled={isLoadingData}
              expedient={expedientDefault}
              onChangeInputExpedient={onChangeInputExpedient}
              ref={inputExpedientRef}
            />

            <InputIndex
              label={t('headers.index')}
              disabled={isLoadingData}
              index={indexDefault}
              onChangeInputIndex={onChangeInputIndex}
              ref={inputIndexRef}
            />

            <InputPK
              label={t('tables:headers.pkInit')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKInit}
              ref={inputPKInitRef}
            />
            <InputPK
              label={t('tables:headers.pkEnd')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKEnd}
              ref={inputPKEndRef}
            />
            {/* Filtro por descripción: texto libre sobre Order.description.
                Busca por subcadena, sin distinguir acentos ni mayúsculas, y
                exige todas las palabras en cualquier orden (el backend filtra
                contra la copia normalizada `descriptionSearch`, indexada). */}
            <InputText
              label={t('tables:headers.description')}
              typeInput="normal"
              inputSize="S"
              placeholder={t('common:placeholders.writeHere')}
              disabled={isLoadingData}
              value={
                selectorData?.description ?? selectorCursor?.description ?? ''
              }
              onChange={(e) =>
                setSelectorData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />

            <InputCalendarStyled
              ref={inputDateRef}
              label={t('tables:filters.dateRangeFilter')}
              name={'data'}
              onChangeInput={onChangeDateInput}
              dateRange={
                selectorCursor?.fromDate && selectorCursor?.toDate
                  ? {
                    from: parseISO(selectorCursor?.fromDate),
                    to: parseISO(selectorCursor?.toDate),
                  }
                  : undefined
              }
              disabled={isLoadingData}
            />

            {concessionOptions && concessionOptions.length > 1 && (
              <DropDownInput
                inputSize="XS"
                ref={inputListConRef}
                labelText={t('tables:headers.concession')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                defaultValue={selectorCursor?.concessionId ?? ''}
                options={concessionOptions}
                onChangeSelected={onChangeSelectedConcession as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            <DropDownInput
              inputSize="XS"
              ref={inputWeatherRef}
              labelText={t('forms:accident.weather')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              defaultValue={selectorCursor?.weatherId ?? ''}
              options={weatherOptions}
              onChangeSelected={onChangeSelectedWeatherGroup as onChangeValue}
              isDisabled={isLoadingData}
            />

            <DropDownInputStyledBig
              inputSize="M"
              ref={inputMediumKnowledge}
              labelText={t('forms:accident.mediumKnowledge')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              defaultValue={selectorCursor?.mediumKnowledgeId ?? ''}
              options={mediumOptions}
              onChangeSelected={onChangeSelectedMediumGroup as onChangeValue}
              isDisabled={isLoadingData}
            />

            {showCapitolFilter && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListCapitolRef}
                labelText={'Capitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.capitolId ?? ''}
                options={capitolOptions() as option[]}
                onChangeSelected={onChangeSelectedCapitol as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            {showSubCapitolFilter && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListSubcapitolRef}
                labelText={'Subcapitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.subCapitolId ?? ''}
                options={subCapitolOptions as option[]}
                onChangeSelected={onChangeSelectedSubCapitol as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            {showPredefinedFilter && predefinedOrderOptions && predefinedOrderOptions.length > 0 && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListPredefinedRef}
                labelText={t('forms:workOrder.predefined')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.predefinedOrderId ?? ''}
                options={predefinedOrderOptions as option[]}
                onChangeSelected={onChangeSelectedPredefinedOrder as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            <MultiSelectDropdownStyled
              inputSize="S"
              ref={inputStateRef}
              labelText={t('tables:headers.status')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              selectedValues={selectorCursor?.states ?? []}
              options={roleActive ? getStateOptionsByRoleAndEntityType(t, roleActive as Role, TABLE_ENTITY_TYPES[tableId as string], listingFiltersByRole(tableId)) : []}
              onChangeSelected={onChangeSelectedStates}
              isDisabled={isLoadingData}
            />
            <DropDownInputStyledBig
              inputSize="S"
              ref={inputLaneCutRef}
              labelText={t('common:tables.headers.laneCut')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              defaultValue={booleanToLaneCutSelection(
                selectorCursor?.laneCutting,
              )}
              options={getLaneCutFilterOptions(t) as option[]}
              onChangeSelected={onChangeSelectedLaneCutting as onChangeValue}
              isDisabled={isLoadingData}
            />

            {userOptions && userOptions.length > 0 && (
              <MultiSelectDropdownStyled
                inputSize="L"
                ref={inputUserRef}
                labelText={t('common:labels.creator')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                searchable
                selectedValues={selectorCursor?.userIds ?? []}
                options={userOptions}
                onChangeSelected={onChangeSelectedUserIds}
                isDisabled={isLoadingData}
              />
            )}

            {/* Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen".
                Oculto en pestañas contextuales (campaña/RV): la página ya
                está fijada a un container concreto o a un tipo fijo. */}
            {!isContainerScopedTable && (
              <MultiSelectDropdownStyled
                inputSize="S"
                ref={inputContainerTypeRef}
                labelText={t('common:tables.headers.origin')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                selectedValues={selectorCursor?.containerTypes ?? []}
                options={getContainerTypeOptions(t)}
                onChangeSelected={onChangeSelectedContainerTypes}
                isDisabled={isLoadingData}
              />
            )}

            {teamOptions && teamOptions.length > 0 && (
              <MultiSelectDropdownStyled
                inputSize="L"
                ref={inputTeamRef}
                labelText={t('forms:workOrder.team')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                searchable
                selectedValues={selectorCursor?.teamIds ?? []}
                options={teamOptions}
                onChangeSelected={onChangeSelectedTeamIds}
                isDisabled={isLoadingData}
              />
            )}

            <ContainerButtonStyled>
              <ButtonIconBackground
                variant={'delete'}
                onClick={onClearValue}
                disabled={isLoadingData}
                title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              />
              <DropDownVisibilityColumn
                columnNames={Object.fromEntries(hideableColumns.map(c => [c.id, translatedColumnNames[c.id as keyof typeof translatedColumnNames]?.label || c.id]))}
                columns={hideableColumns}
                isDisabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'restart'}
                onClick={() => { setCurrentServerPage(1); onReFetchData?.(); }}
                disabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'search'}
                onClick={handleSearch}
                disabled={isLoadingData}
                title={`${t('buttons:search')} (Enter)`}
              />
            </ContainerButtonStyled>
            <IncludeInactiveToggle
              checked={selectorCursor?.includeInactive ?? false}
              onChange={onChangeIncludeInactive}
              label={t('common:filters.includeInactiveInCreator')}
              isDisabled={isLoadingData}
            />
            {showOnlyFlaggedFilter && (
              <IncludeInactiveToggle
                checked={Boolean(selectorCursor?.onlyFlagged)}
                onChange={onChangeOnlyFlagged}
                label={flagScopeTexts.onlyFlagged}
                isDisabled={isLoadingData}
              />
            )}
          </SubContainerFiltersStyled>
        )}
      </FiltersWithLoadingStyled>
      <TableContainerStyled isLoading={isLoadingData}>
        <Table {...props}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    {...exportIgnoreProps(header.column.id)}
                    aria-sort={
                      header.column.id === activeSortColumnId
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </Th>
                ))}
              </tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <DoubleTapRow
                  key={row.id}
                  StyledComponent={TrStyled}
                  componentProps={{
                    checked: row.getIsSelected(),
                    indeterminate: !table.getIsSomeRowsSelected(),
                  }}
                  isDisabled={isLoadingData as boolean}
                  onSingleTap={() => {
                    handleClickRow(row);
                    row.getToggleSelectedHandler()(row);
                  }}
                  onDoubleTap={() => {
                    if (!row.original?.id) return;
                    // La pantalla sabe adónde se vuelve: si lo dice, manda ella.
                    if (onRowOpen) {
                      onRowOpen(row.original.id);
                      return;
                    }
                    navigate(`/work-orders/accidents/${row.original.id}`);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TdStyled
                      column={cell.column.id as columnID}
                      hiddenColumns={hiddenColumns}
                      key={cell.id}
                      {...exportIgnoreProps(cell.column.id)}
                    >
                      <>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </>
                    </TdStyled>
                  ))}
                </DoubleTapRow>
              );
            })}
          </Tbody>
        </Table>
      </TableContainerStyled>
      {isCheckboxMode && onSelectAll && onClearSelection && (
        <SelectionBanner
          selectedCount={propsSelectedCount}
          totalCount={data?.totalCount || 0}
          pageCount={dataAll.length}
          allPageSelected={
            dataAll.length > 0 &&
            dataAll.every((row: any) => (rowSelection as Record<string, boolean>)[row.id])
          }
          selectAllMode={selectAllMode}
          onSelectAll={onSelectAll}
          onClearSelection={onClearSelection}
        />
      )}
      <PaginationButtons
        pageCount={Math.max(1, Math.ceil((data?.totalCount ?? 0) / takePagination))}
        currentPage={currentServerPage}
        totalCount={data.totalCount}
        nextPage={nextPagination}
        previousPage={backPagination}
        isDisabled={isLoadingData}
        // Tarea #11: el selector aparece SOLO en escritorio. `paginationProps`
        // viene de `useServerPageSize` y ya tiene los gates aplicados (en
        // tablet o tableId modal los 3 props van como undefined).
        {...paginationProps}
      />
    </ContainerStyled>
  );
};
